import axios from "axios";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import Swal from "sweetalert2";

const URL_ARTICULOS_SURTIDOS = process.env.REACT_APP_URL_ARTICULOS_SURTIDOS;

/**
 * Genera y abre/imprime el ticket de una venta.
 * Acepta args por nombre (objeto) para no depender del orden posicional.
 */
export async function imprimirTicketVenta({
  fecha,
  clientes,
  observaciones,
  numero,
  idPDFOC, // surtido._id
  pedidoCliente,
  subTotal,
  iva,
  total_general,
  totalCantidad,
  descuento,
  nombreColaborador,
  sucursalColaborador,
  areaColaborador,
  numeroColaborador,
  entregadoCon,
  esVentaDirecta,
  alumno,
  formaPago,
  vendedor,
  idPedido,
  autoPrint = false,
}) {
  try {
    const { data: allArticulosSurtido } = await axios.get(
      `${URL_ARTICULOS_SURTIDOS}/surtido/${idPDFOC}`,
      {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      }
    );

    const safeText = (txt) => String(txt || "").trim();

    const ordenTallas = ["2XCH","XCH","EXTRA CHICA","CHICA","CH","M","MEDIANA","GRANDE","G","EXTRA GRANDE","XG","2XG","XXG","3XG","XXXG","4XG","XXXXG","5XG","XXXXXG","ESP","VARIOS","2","4","6","8","9","10","11","12","13","14","15","16","17","18","19","20","21","22","23","24","25","26","27","28","29","30","31","32","33","34","35","36","37","38","39","40","41","42","43","44","45","46","47","48","49","50","51","52","53","54","55","56"];

    const agrupado = {};

    allArticulosSurtido.forEach((a) => {
      const articulo = `${a.articulos?.[0]?.codigo || ""} ${
        a.articulos?.[0]?.descripcion ||
        a.articulos?.[0]?.nombre ||
        "Sin artículo"
      }`.trim();

      // Si es Venta Directa agrupamos por articulo + precio para que dos
      // ventas del mismo articulo con precios distintos salgan en renglones
      // separados. Para tickets normales seguimos agrupando solo por articulo.
      const esVDKey = String(esVentaDirecta || "").toLowerCase() === "si";
      const key = esVDKey
        ? `${articulo}||${Number(a.precio || 0).toFixed(2)}`
        : articulo;

      if (!agrupado[key]) {
        agrupado[key] = [];
        agrupado[key].articulo = articulo;
      }
      agrupado[key].push(a);
    });

    const esVD = String(esVentaDirecta || "").toLowerCase() === "si";

    const formaPagoLabelMap = {
      "01": "Efectivo",
      "02": "Cheque nominativo",
      "03": "Transferencia",
      "04": "Tarjeta de credito",
      "05": "Monedero electronico",
      "06": "Dinero electronico",
      "08": "Vales de despensa",
      "12": "Dacion en pago",
      "13": "Pago por subrogacion",
      "14": "Pago por consignacion",
      "15": "Condonacion",
      "17": "Compensacion",
      "23": "Novacion",
      "24": "Confusion",
      "25": "Remision de deuda",
      "26": "Prescripcion o caducidad",
      "27": "A satisfaccion del acreedor",
      "28": "Tarjeta de debito",
      "29": "Tarjeta de servicios",
      "30": "Aplicacion de anticipos",
      "31": "Intermediario pagos",
      "99": "Por definir",
    };
    const formaPagoTxt = (() => {
      const codeRaw = safeText(formaPago);
      if (!codeRaw || codeRaw.toUpperCase() === "NA") return "";
      const label = formaPagoLabelMap[codeRaw] || "";
      return label ? `(${codeRaw})${label}` : codeRaw;
    })();

    const numeroLetras = (() => {
      try {
        const n = Math.floor(Number(total_general) || 0);
        const c = Math.round(((Number(total_general) || 0) - n) * 100);
        const u = [
          "CERO","UNO","DOS","TRES","CUATRO","CINCO","SEIS","SIETE","OCHO","NUEVE","DIEZ",
          "ONCE","DOCE","TRECE","CATORCE","QUINCE","DIECISEIS","DIECISIETE","DIECIOCHO","DIECINUEVE","VEINTE",
        ];
        const dec = ["","","VEINTE","TREINTA","CUARENTA","CINCUENTA","SESENTA","SETENTA","OCHENTA","NOVENTA"];
        const cen = ["","CIENTO","DOSCIENTOS","TRESCIENTOS","CUATROCIENTOS","QUINIENTOS","SEISCIENTOS","SETECIENTOS","OCHOCIENTOS","NOVECIENTOS"];
        const sub999 = (x) => {
          if (x === 0) return "";
          if (x <= 20) return u[x];
          if (x < 100) {
            const d = Math.floor(x / 10);
            const r = x % 10;
            if (x < 30) return `VEINTI${u[r].toLowerCase()}`.toUpperCase();
            return r === 0 ? dec[d] : `${dec[d]} Y ${u[r]}`;
          }
          if (x === 100) return "CIEN";
          const c100 = Math.floor(x / 100);
          const r = x % 100;
          return r === 0 ? cen[c100] : `${cen[c100]} ${sub999(r)}`;
        };
        const enLetras = (x) => {
          if (x === 0) return "CERO";
          let result = "";
          const mil = Math.floor(x / 1000);
          const r = x % 1000;
          if (mil > 0) {
            result = mil === 1 ? "MIL" : `${sub999(mil)} MIL`;
          }
          if (r > 0) {
            result = result ? `${result} ${sub999(r)}` : sub999(r);
          }
          return result.trim();
        };
        const ent = enLetras(n);
        const cc = String(c).padStart(2, "0");
        return `${ent} PESOS ${cc}/100 M.N.`;
      } catch (_) {
        return "";
      }
    })();

    const fmt = (v) =>
      new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(v) || 0);

    const construirLineasTicket = () => {
      const lineas = [];

      lineas.push({ type: "logo" });

      if (esVD) {
        if (safeText(alumno)) {
          lineas.push({ type: "text", text: `Nombre: ${safeText(alumno)}` });
        }
        lineas.push({ type: "text", text: `Cliente: ${clientes}` });
        lineas.push({ type: "line" });
        if (safeText(sucursalColaborador)) {
          lineas.push({
            type: "text",
            text: `Tienda: ${safeText(sucursalColaborador)}`,
          });
        }
        lineas.push({
          type: "text",
          text: `Folio: ${String(idPedido || numero || "").padStart(10, "0")}`,
        });
        lineas.push({ type: "text", text: `Fecha: ${fecha}` });
        const horaStr = new Date().toLocaleTimeString("es-MX", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        });
        lineas.push({ type: "text", text: `Hora: ${horaStr}` });
        lineas.push({
          type: "text",
          text: `Vendedor: ${safeText(vendedor || nombreColaborador)}`,
        });
        lineas.push({ type: "line" });
        lineas.push({ type: "tableHeader" });
        lineas.push({ type: "line" });
      } else {
        lineas.push({ type: "title", text: "TICKET" });
        lineas.push({ type: "line" });
        lineas.push({ type: "text", text: `Cliente: ${clientes}` });
        lineas.push({ type: "text", text: `Fecha: ${fecha}` });
        lineas.push({ type: "text", text: `Nota: ${numero}` });
        lineas.push({ type: "text", text: `Pedido: ${pedidoCliente}` });
        lineas.push({
          type: "text",
          text: `Colaborador: ${safeText(nombreColaborador)}`,
        });

        if (safeText(numeroColaborador)) {
          lineas.push({
            type: "text",
            text: `Número: ${safeText(numeroColaborador)}`,
          });
        }

        if (safeText(sucursalColaborador)) {
          lineas.push({
            type: "text",
            text: `Sucursal: ${safeText(sucursalColaborador)}`,
          });
        }

        if (safeText(areaColaborador)) {
          lineas.push({
            type: "text",
            text: `Area: ${safeText(areaColaborador)}`,
          });
        }

        if (safeText(entregadoCon)) {
          lineas.push({
            type: "text",
            text: `Entregado con: ${safeText(entregadoCon)}`,
          });
        }

        lineas.push({ type: "text", text: `Cantidad total: ${totalCantidad}` });
        lineas.push({ type: "line" });
      }

      if (esVD) {
        let totalPrendas = 0;
        for (const key in agrupado) {
          const grupo = agrupado[key];
          const articuloNombre = grupo.articulo || key.split("||")[0];
          let cantArticulo = 0;
          let importeArticulo = 0;
          let pu = 0;
          grupo.forEach((a) => {
            const c = Number(a.cantidad || 0);
            const p = Number(a.precio || 0);
            cantArticulo += c;
            importeArticulo += c * p;
            if (!pu && p) pu = p;
          });
          totalPrendas += cantArticulo;
          lineas.push({
            type: "tableRow",
            cant: cantArticulo,
            descripcion: articuloNombre,
            pu: fmt(pu),
            importe: fmt(importeArticulo),
          });
        }

        lineas.push({ type: "line" });
        lineas.push({
          type: "textBold",
          text: `${totalPrendas} Prenda(s)`,
        });
        lineas.push({ type: "line" });
        lineas.push({
          type: "text",
          text: `SubTotal:                ${fmt(subTotal || total_general)}`,
        });
        lineas.push({
          type: "text",
          text: `Descuento:               ${fmt(descuento || 0)}`,
        });
        lineas.push({
          type: "text",
          text: `Impuesto:                ${fmt(iva || 0)}`,
        });
        lineas.push({ type: "line" });
        lineas.push({
          type: "textBold",
          text: `Total:                   ${fmt(total_general)}`,
        });
        lineas.push({ type: "line" });

        if (formaPagoTxt) {
          lineas.push({ type: "text", text: `Forma de Pago: ${formaPagoTxt}` });
        }
        lineas.push({ type: "text", text: `Tipo de Venta: MOSTR` });
        lineas.push({ type: "line" });

        if (numeroLetras) {
          lineas.push({ type: "textWrap", text: numeroLetras });
          lineas.push({ type: "line" });
        }

        if (observaciones && safeText(observaciones).toUpperCase() !== "NA") {
          lineas.push({ type: "textBold", text: "Observaciones:" });
          lineas.push({ type: "textWrap", text: observaciones });
          lineas.push({ type: "line" });
        }

        lineas.push({ type: "center", text: "Gracias por su compra :)" });

        return lineas;
      }

      for (const articulo in agrupado) {
        const grupo = agrupado[articulo];

        lineas.push({ type: "section", text: articulo });

        const agrupadoPorColor = {};
        const tallasMap = new Map();

        grupo.forEach((a) => {
          const color = a.colores?.[0]?.name || "Sin color";
          const tallaBase = safeText(a.tallas?.[0]?.name) || "Sin talla";
          const variante = safeText(a.tallas?.[0]?.variantesTallas?.[0]?.name);

          const tallaCompleta = variante
            ? `${tallaBase} (${variante})`
            : tallaBase;

          if (!agrupadoPorColor[color]) agrupadoPorColor[color] = {};
          if (!agrupadoPorColor[color][tallaCompleta]) {
            agrupadoPorColor[color][tallaCompleta] = 0;
          }

          agrupadoPorColor[color][tallaCompleta] += Number(a.cantidad || 0);
          tallasMap.set(tallaCompleta, tallaBase);
        });

        const tallasOrdenadas = Array.from(tallasMap.keys()).sort((a, b) => {
          const baseA = tallasMap.get(a) || a;
          const baseB = tallasMap.get(b) || b;

          const indexA = ordenTallas.indexOf(baseA);
          const indexB = ordenTallas.indexOf(baseB);

          return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
        });

        let totalPiezasArticulo = 0;

        Object.keys(agrupadoPorColor).forEach((color) => {
          lineas.push({ type: "textBold", text: `Color: ${color}` });

          tallasOrdenadas.forEach((talla) => {
            const cantidad = agrupadoPorColor[color][talla] || 0;

            if (cantidad > 0) {
              totalPiezasArticulo += cantidad;
              lineas.push({
                type: "item",
                text: `${talla}: ${cantidad}`,
              });
            }
          });
        });

        lineas.push({
          type: "textBold",
          text: `Piezas artículo: ${totalPiezasArticulo}`,
        });
        lineas.push({ type: "line" });
      }

      if (observaciones) {
        lineas.push({ type: "textBold", text: "Observaciones:" });
        lineas.push({ type: "textWrap", text: observaciones });
        lineas.push({ type: "line" });
      }

      lineas.push({ type: "center", text: "Gracias por su compra" });

      return lineas;
    };

    const lineasTicket = construirLineasTicket();

    const calcularAltura = (lineas) => {
      let altura = 25;

      lineas.forEach((l) => {
        if (l.type === "logo") {
          altura += 18;
        } else if (l.type === "textWrap") {
          const extra = Math.ceil(String(l.text || "").length / 30);
          altura += extra * 4.2;
        } else if (l.type === "title") {
          altura += 6;
        } else if (l.type === "line") {
          altura += 3;
        } else if (l.type === "tableRow") {
          const extra = Math.max(
            1,
            Math.ceil(String(l.descripcion || "").length / 18)
          );
          altura += extra * 4.2;
        } else {
          altura += 4.2;
        }
      });

      return altura + 10;
    };

    const alturaTotal = calcularAltura(lineasTicket);

    const doc = new jsPDF({
      orientation: "p",
      unit: "mm",
      format: [80, alturaTotal],
    });

    const pageWidth = 80;
    const marginX = 4;
    const maxWidth = pageWidth - marginX * 2;

    const printWrapped = (text, x, currentY, opts = {}) => {
      const { fontSize = 8, style = "bold", align = "left" } = opts;

      doc.setFont("courier", style);
      doc.setFontSize(fontSize);

      const lines = doc.splitTextToSize(String(text || ""), maxWidth);

      lines.forEach((line) => {
        if (align === "center") {
          doc.text(line, pageWidth / 2, currentY, { align: "center" });
        } else {
          doc.text(line, x, currentY);
        }
        currentY += 4;
      });

      return currentY;
    };

    const dibujarLineas = (lineas, yInicial) => {
      let y = yInicial;

      for (const linea of lineas) {
        switch (linea.type) {
          case "logo": {
            try {
              const img1 = new Image();
              img1.src = process.env.PUBLIC_URL + "/logoInstitucion.png";
              doc.addImage(img1, "PNG", 20, y, 40, 12);
              y += 15;
            } catch (e) {
              y += 2;
            }
            break;
          }

          case "title":
            y = printWrapped(linea.text, marginX, y, {
              fontSize: 11,
              style: "bold",
              align: "center",
            });
            y += 1;
            break;

          case "section":
            y = printWrapped(linea.text, marginX, y, {
              fontSize: 10,
              style: "bold",
            });
            break;

          case "textBold":
            y = printWrapped(linea.text, marginX, y, {
              fontSize: 10,
              style: "bold",
            });
            break;

          case "center":
            y = printWrapped(linea.text, marginX, y, {
              fontSize: 8,
              style: "bold",
              align: "center",
            });
            break;

          case "textWrap":
            y = printWrapped(linea.text, marginX, y, {
              fontSize: 8,
              style: "bold",
            });
            break;

          case "item":
            y = printWrapped(`  ${linea.text}`, marginX, y, {
              fontSize: 8,
              style: "bold",
            });
            break;

          case "line":
            doc.setLineWidth(0.2);
            doc.line(marginX, y, pageWidth - marginX, y);
            y += 3;
            break;

          case "tableHeader": {
            doc.setFont("helvetica", "bold");
            doc.setFontSize(8);
            const xCant = marginX;
            const xDesc = marginX + 12;
            const xPU = pageWidth - marginX - 22;
            const xImp = pageWidth - marginX;
            doc.text("Cant.", xCant, y);
            doc.text("Descripcion", xDesc, y);
            doc.text("P.U.", xPU, y, { align: "right" });
            doc.text("Importe", xImp, y, { align: "right" });
            y += 4.2;
            break;
          }

          case "tableRow": {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            const xCant = marginX;
            const xDesc = marginX + 12;
            const xPU = pageWidth - marginX - 22;
            const xImp = pageWidth - marginX;
            const descMaxWidth = xPU - xDesc - 4;
            const descLines = doc.splitTextToSize(
              String(linea.descripcion || ""),
              descMaxWidth
            );
            doc.text(String(linea.cant), xCant, y);
            doc.text(descLines, xDesc, y);
            doc.text(String(linea.pu), xPU, y, { align: "right" });
            doc.text(String(linea.importe), xImp, y, { align: "right" });
            y += descLines.length * 4.2;
            break;
          }

          default:
            y = printWrapped(linea.text, marginX, y, {
              fontSize: 8,
              style: "bold",
            });
            break;
        }
      }

      return y;
    };

    let y = 6;
    dibujarLineas(lineasTicket, y);

    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);

    const printWindow = window.open(pdfUrl);

    if (printWindow) {
      printWindow.onload = function () {
        printWindow.focus();
        if (autoPrint) {
          printWindow.print();
        }
      };
    }
  } catch (err) {
    console.error("Error generando ticket:", err);
    Swal.fire("Error", "No se pudo generar el ticket", "error");
  }
}
